import {
  Box, Button, HStack, IconButton, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, Spinner, Text,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Trans } from "@lingui/react/macro";
import {
  IndexName, PRODUCT_DESCRIPTIONS, PRODUCT_LABELS, SATELLITE_PRODUCTS, isScore, observationDay, satelliteGradient,
} from "../../utils/satelliteProducts";

type Entry = {
  satellite_data_id: number;
  acquisition_date?: string | null;
  ingestion_date?: string | null;
  created_at: string;
};

type Props = {
  indexName: IndexName;
  valueRange: [number, number];
  /** Entries with the current index, newest first. */
  entries: Entry[];
  selectedId: number | null;
  isLoading: boolean;
  onSelect: (satelliteDataId: number) => void;
  /** Indices that have at least one image for this field. */
  availableProducts: IndexName[];
  onIndexChange: (indexName: string) => void;
};

const formatValue = (name: IndexName, value: number) =>
  isScore(name) ? String(Math.round(value)) : value.toFixed(2);

export default function SatelliteMapCard({
  indexName, valueRange, entries, selectedId, isLoading, onSelect, availableProducts, onIndexChange,
}: Props) {
  const index = entries.findIndex((entry) => entry.satellite_data_id === selectedId);
  const selected = entries[index];
  const older = index >= 0 ? entries[index + 1] : undefined;
  const newer = index > 0 ? entries[index - 1] : undefined;

  return (
    <Box
      position="absolute" bottom={10} right={3} zIndex={3} w="260px"
      bg="white" color="black" borderRadius="md" boxShadow="md" px={3} py={2}
    >
      <HStack justify="space-between" mb={1}>
        <Menu placement="top-start" isLazy>
          <MenuButton
            as={Button} size="sm" variant="ghost" color="black" ml={-2} px={2}
            rightIcon={<ChevronDownIcon />} fontWeight="bold"
          >
            {PRODUCT_LABELS[indexName]}
          </MenuButton>
          <MenuList color="black" bg="white" maxW="300px" zIndex={2}>
            <MenuOptionGroup type="radio" value={indexName} onChange={(value) => onIndexChange(String(value))}>
              {SATELLITE_PRODUCTS.map((product) => (
                <MenuItemOption key={product} value={product} isDisabled={!availableProducts.includes(product)}>
                  <Text fontWeight="semibold" fontSize="sm">{PRODUCT_LABELS[product]}</Text>
                  <Text fontSize="xs" color="gray.600" whiteSpace="normal">{PRODUCT_DESCRIPTIONS[product]}</Text>
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
        {isLoading && <Spinner size="xs" />}
      </HStack>

      <HStack spacing={1} mb={2}>
        <IconButton
          aria-label="Older image" size="xs" variant="ghost" icon={<ChevronLeftIcon boxSize={5} />}
          isDisabled={!older} onClick={() => older && onSelect(older.satellite_data_id)}
        />
        <Text flex="1" textAlign="center" fontSize="sm">
          {selected ? observationDay(selected) : <Trans>No image</Trans>}
        </Text>
        <IconButton
          aria-label="Newer image" size="xs" variant="ghost" icon={<ChevronRightIcon boxSize={5} />}
          isDisabled={!newer} onClick={() => newer && onSelect(newer.satellite_data_id)}
        />
      </HStack>

      <Box h="10px" borderRadius="sm" bgImage={satelliteGradient(indexName, valueRange)} />
      <HStack justify="space-between" fontSize="xs" color="gray.600" mt={1}>
        <Text>{formatValue(indexName, valueRange[0])}</Text>
        {(indexName === "NDWI" || indexName === "NDMI") ? (
          <Text><Trans>Dry → Wet</Trans></Text>
        ) : indexName === "current_stress" ? (
          <Text><Trans>Low → High stress</Trans></Text>
        ) : (
          <Text><Trans>Poor → Good</Trans></Text>
        )}
        <Text>{formatValue(indexName, valueRange[1])}</Text>
      </HStack>
    </Box>
  );
}
